import { z } from "zod";

/**
 * Schema de validação Zod para formulário de criação de filial
 * Baseado no schema de cliente, mas com campos específicos para filial
 */
export const createFilialSchema = z.object({
  // Dados básicos (obrigatórios)
  nomeFantasia: z
    .string()
    .min(2, "Nome fantasia deve ter pelo menos 2 caracteres")
    .max(255, "Nome fantasia não pode exceder 255 caracteres"),
  
  razaoSocial: z
    .string()
    .min(2, "Razão social deve ter pelo menos 2 caracteres")
    .max(255, "Razão social não pode exceder 255 caracteres"),
  
  cnpj: z
    .string()
    .min(14, "CNPJ deve ter 14 dígitos")
    .max(20, "CNPJ inválido")
    .refine(
      (val) => /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$|^\d{14}$/.test(val),
      "CNPJ deve estar no formato: 12.345.678/0001-99 ou 12345678000199"
    ),
  
  // Endereço (obrigatórios)
  logradouro: z
    .string()
    .min(2, "Logradouro deve ter pelo menos 2 caracteres")
    .max(255, "Logradouro não pode exceder 255 caracteres"),
  
  numero: z
    .string()
    .min(1, "Número é obrigatório")
    .max(20, "Número não pode exceder 20 caracteres"),
  
  bairro: z
    .string()
    .min(2, "Bairro deve ter pelo menos 2 caracteres")
    .max(100, "Bairro não pode exceder 100 caracteres"),
  
  cidade: z
    .string()
    .min(2, "Cidade deve ter pelo menos 2 caracteres")
    .max(100, "Cidade não pode exceder 100 caracteres"),
  
  estado: z
    .string()
    .length(2, "Estado deve ter 2 caracteres (ex: SP)")
    .toUpperCase(),
  
  cep: z
    .string()
    .refine(
      (val) => /^\d{5}-?\d{3}$/.test(val),
      "CEP deve estar no formato: 12345-678 ou 12345678"
    ),
  
  // Endereço (opcionais)
  complemento: z
    .string()
    .max(100, "Complemento não pode exceder 100 caracteres")
    .optional()
    .or(z.literal("")),
  
  // NR-1 (opcionais)
  cnae: z
    .string()
    .max(10, "CNAE não pode exceder 10 caracteres")
    .optional()
    .or(z.literal("")),
  
  atividadePrincipal: z
    .string()
    .max(500, "Atividade principal não pode exceder 500 caracteres")
    .optional()
    .or(z.literal("")),
  
  grauRisco: z
    .string()
    .max(10, "Grau de risco não pode exceder 10 caracteres")
    .optional()
    .or(z.literal("")),
  
  sector: z
    .string()
    .max(100, "Setor não pode exceder 100 caracteres")
    .optional()
    .or(z.literal("")),
  
  employeesCount: z
    .number()
    .int("Quantidade de colaboradores deve ser um número inteiro")
    .min(0, "Quantidade de colaboradores não pode ser negativa")
    .optional(),
  
  // Contato (opcionais)
  email: z
    .string()
    .email("Email inválido")
    .optional()
    .or(z.literal("")),
  
  phone: z
    .string()
    .max(20, "Telefone não pode exceder 20 caracteres")
    .optional()
    .or(z.literal("")),
  
  responsibleName: z
    .string()
    .max(255, "Nome do responsável não pode exceder 255 caracteres")
    .optional()
    .or(z.literal("")),
  
  // Campos automáticos (read-only na UI)
  matrizId: z
    .number()
    .int("matrizId deve ser um número inteiro")
    .optional(),
  
  controladoraId: z
    .number()
    .int("controladoraId deve ser um número inteiro")
    .nullable()
    .optional(),
});

export type CreateFilialFormData = z.infer<typeof createFilialSchema>;

/**
 * Schema para validação de campos obrigatórios de filial
 * Usado para validar antes de enviar ao backend
 */
export const createFilialRequiredSchema = createFilialSchema.pick({
  nomeFantasia: true,
  razaoSocial: true,
  cnpj: true,
  logradouro: true,
  numero: true,
  bairro: true,
  cidade: true,
  estado: true,
  cep: true,
});

export type CreateFilialRequiredData = z.infer<typeof createFilialRequiredSchema>;
